import * as _ from 'lodash';
import * as React from 'react';
import * as ReactRouter from 'react-router';
import { diagnostics, utils } from 'ui-common';
import { colors, combineStyles, fonts, media, mixins } from 'consumer-components';
import { css, StyleSheet } from 'aphrodite';
import * as queryString from 'query-string';

import DetailsModal from './DetailsModal/DetailsModal';
import TextScreenModalTrigger from 'login-components/Common/TextScreenModal/TextScreenModalTrigger';
import { formPost } from 'login-apis/formPost';
import * as privacyPolicyAPI from 'login-apis/privacyPolicyAPI';
import { WindowWrapper } from 'login-utils/globalWrappers';
import * as accountOpeningAPI from 'login-apis/accountOpeningAPI';
import * as cookie from 'react-cookie';
import { myFreeDetails, myStyleDetails, traditionalSavingsDetails } from './DetailsModal/DetailsModalContent';
import { generateInnerHtml } from 'shared-react';

const { featureFlags } = utils;

const productToDetailsMap = {
  '01': traditionalSavingsDetails,
  51: myFreeDetails,
  52: myStyleDetails,
};

const ACTION_COLOR = WindowWrapper.DEPLOYMENT_CONFIG.organization.colors.action;

const styles = StyleSheet.create({
  root: {
    width: 560,
    [media.mobileMedium]: {
      width: '100%',
    },
  },
  card: combineStyles(mixins.border(2, 'solid', colors.white), mixins.borderRadius(10), {
    alignItems: 'center',
    backgroundColor: colors.white,
    boxShadow: mixins.boxShadow(0, 0, 6, 0, mixins.rgba(0, 0, 0, 0.24)),
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 20,
    minHeight: 157,
    width: '100%',
    [media.mobileMedium]: {
      alignItems: 'stretch',
      minHeight: 'inherit',
      paddingBottom: 20,
      paddingTop: 15,
    },
  }),
  // required for IE flexbox bug
  cardFlexWrapper: {
    display: 'flex',
  },
  actionButton: {
    ...mixins.removeButtonStyling(),
    ...fonts.Gotham,
    ...mixins.borderRadius(4),
    color: colors.white,
    height: 34,
    fontSize: 14,
    fontWeight: fonts.weight.medium,
    letterSpacing: 0,
    marginBottom: 19,
    backgroundColor: ACTION_COLOR,
    width: 125,
    ':hover': { color: colors.white },
    ':focus': { color: colors.white },
    [media.mobileMedium]: {
      height: 30,
      fontSize: 12,
      marginTop: 0,
      width: 70,
    },
  },
  linkButton: {
    ...mixins.removeButtonStyling(),
    ...fonts.Gotham,
    background: 'none',
    color: ACTION_COLOR,
    fontSize: 12,
    fontWeight: fonts.weight.medium,
    letterSpacing: 0,
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    [media.mobileMedium]: {
      ...fonts.Roboto,
      fontSize: 14,
      fontWeight: fonts.weight.normal,
      letterSpacing: 0, // need to re-specify after updating font mixin
    },
  },
  cardTitle: {
    color: colors.darkerGray,
    fontSize: 18,
    fontWeight: fonts.weight.bold,
    marginBottom: 4,
  },
  cardDescription: {
    color: colors.clayPlus3,
    fontWeight: fonts.weight.book,
    fontSize: 14,
    paddingTop: 5,
  },
  cardColumnLeft: {
    ...fonts.Roboto,
    marginLeft: 28,
    marginRight: 55,
    textAlign: 'left',
    [media.mobileMedium]: {
      marginLeft: 16,
      marginRight: 19,
    },
  },
  cardColumnRight: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: 32,
    textAlign: 'center',
    [media.mobileMedium]: {
      marginRight: 15,
    },
  },
  footer: {
    ...fonts.Roboto,
    color: colors.clayPlus3,
    display: 'inline',
    fontSize: 12,
  },
  footerLink: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
});

interface AccountTypeCardProps {
  isDisabled: boolean;
  hasDetails: boolean;
  title: string;
  description: string;
  onClick(Event): void;
  openModal(): void;
}

interface QueryProps {
  promoCode?: string;
}

const AccountTypeCard: React.SFC<AccountTypeCardProps> = ({
  description,
  hasDetails,
  isDisabled,
  onClick,
  openModal,
  title,
}) => {
  return (
    <div className={css(styles.cardFlexWrapper)}>
      <div className={css(styles.card)}>
        <div className={css(styles.cardColumnLeft)}>
          <h2 className={css(styles.cardTitle)}>{title}</h2>
          <p className={css(styles.cardDescription)}>{description}</p>
        </div>
        <div className={css(styles.cardColumnRight)}>
          <button onClick={onClick} className={css(styles.actionButton)} disabled={isDisabled}>
            Apply
          </button>
          {hasDetails && (
            <button className={css(styles.linkButton)} onClick={openModal}>
              View details
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface AccountOpeningState {
  isModalOpen: boolean;
  isApplyDisabled: boolean;
  modalContent: string | JSX.Element;
  modalTitle: string;
  modalNotes: string[];
  products: accountOpeningAPI.Product[];
}

export default class AccountOpening extends React.Component<
  ReactRouter.RouteComponentProps<{}, {}>,
  AccountOpeningState
> {
  state: AccountOpeningState = {
    isModalOpen: false,
    isApplyDisabled: false,
    modalContent: '',
    modalTitle: '',
    modalNotes: [],
    products: [],
  };

  async componentDidMount() {
    try {
      const { data: products } = await accountOpeningAPI.getProducts();
      const formattedProducts: accountOpeningAPI.Product[] = featureFlags.isEnabled(
        'enableAccountOpeningDCSIntegration04092019',
      )
        ? (products as accountOpeningAPI.Product[])
        : _.map<accountOpeningAPI.ProductOld, accountOpeningAPI.Product>(
            products as accountOpeningAPI.ProductOld[],
            this.formatProductsToNewSchema,
          );
      this.setState({ products: formattedProducts });
    } catch (err) {
      // istanbul ignore next
      diagnostics.error(err);
    }
  }

  // TODO AO-534: deprecate this when the new config schema is used everywhere
  formatProductsToNewSchema(product: accountOpeningAPI.ProductOld): accountOpeningAPI.Product {
    return {
      title: product.text,
      description: product.subText,
      accounts: product.accounts,
      referenceId: product.referenceId,
      detailsDisplay: {
        body: productToDetailsMap[product.referenceId!],
        title: product.title,
        footers: product.notes,
      },
    };
  }

  // TODO: AO-101. Remove object-to-formdata dependency and use an actual form to post this
  onSubmit(product: accountOpeningAPI.Product) {
    const _csrf = cookie.load('XSRF-TOKEN');
    const { accounts, referenceId } = product;
    const { location } = this.props;
    const { promoCode } = queryString.parse(location.search) as QueryProps;
    this.setState(
      {
        isApplyDisabled: true,
      },
      () =>
        formPost('/api/public/signup/account-opening', { accounts, _csrf, promoCode, productReferenceId: referenceId }),
    );
  }

  openModal(product: accountOpeningAPI.Product) {
    this.setState({
      isModalOpen: true,
      modalContent: _.get(product, 'detailsDisplay.body'),
      modalTitle: _.get(product, 'detailsDisplay.title') || product.title,
      modalNotes: _.get(product, 'detailsDisplay.footers') /* istanbul ignore next */ || [],
    });
  }

  closeModal() {
    this.setState({ isModalOpen: false });
  }

  render() {
    const { isModalOpen, isApplyDisabled, modalContent, modalNotes, modalTitle, products } = this.state;
    // Wait until products are fetched to render inner content
    if (_.isEmpty(products)) return null;

    return (
      <div className={css(styles.root)}>
        {_.map(products, product => (
          <AccountTypeCard
            title={product.title}
            description={product.description}
            onClick={() => this.onSubmit(product)}
            openModal={() => this.openModal(product)}
            isDisabled={isApplyDisabled}
            hasDetails={!!_.get(product, 'detailsDisplay.body')}
          />
        ))}
        {!featureFlags.isEnabled('enableAccountOpeningPromoCode02042019') && (
          <p className={css(styles.footer)}>
            By clicking "Apply," I confirm that I have read, understood, and consent to the&nbsp;
            <TextScreenModalTrigger
              textScreenId={'terms'}
              headerText="Terms Of Use"
              renderTrigger={({ openModal }) => (
                <span className={css(styles.footer, styles.footerLink)} onClick={openModal}>
                  Terms of Use
                </span>
              )}
            />
            ,&nbsp;
            <TextScreenModalTrigger
              textScreenId={privacyPolicyAPI.getPrivacyPolicyTextScreenId()}
              headerText="Privacy Policy"
              renderTrigger={({ openModal }) => (
                <span className={css(styles.footer, styles.footerLink)} onClick={openModal}>
                  Privacy Policy
                </span>
              )}
            />
            , and&nbsp;
            <TextScreenModalTrigger
              textScreenId={'required-blend-e-consent'}
              headerText="ESIGN Act Consent"
              renderTrigger={({ openModal }) => (
                <span className={css(styles.footer, styles.footerLink)} onClick={openModal}>
                  ESIGN Act Consent
                </span>
              )}
            />
            .
          </p>
        )}
        <DetailsModal
          isOpen={isModalOpen}
          handleClose={() => this.closeModal()}
          title={modalTitle}
          footerNotes={modalNotes}
        >
          {_.isString(modalContent) ? <div {...generateInnerHtml(modalContent)} /> : modalContent}
        </DetailsModal>
      </div>
    );
  }
}
